import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ENDPOINTS } from '../Seguridad/Utils/Endpoints';
import RoleModal from './RoleModal';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import { catRolesNormalize } from '../Seguridad/Objetos/CatRolesModel';
// import { Models } from '../../utils/Models'; // DEPRECATED
import { formatKeys, getKeysData } from '../../utils/Keys';
import { normalizeValue } from '../../utils/Texts';
import AlertDialog from '../Lms/Components/Shared/AlertDialog';
import { getSessionEmpresaId } from '../../utils/SessionUser';

export default function RolesCatalog() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);

  // --- Paginación ---
  const PAGE_SIZE = 7;
  const [page, setPage] = useState(1);
  const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

  const triggerAlert = (message, variant = 'error', title = 'Error') => {
    setModalOpen(false);
    setDeleteOpen(false);
    setAlertConfig({ open: true, message, variant, title });
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const resEmpresas = await axios.get(ENDPOINTS.empresas);
      const emps = resEmpresas.data || [];
      const empIndex = {};
      emps.forEach(e => empIndex[e.idEmpresa] = e);

      const res = await axios.get(`${ENDPOINTS.roles}?soloActivos=false`);
      const normalized = (res.data || []).map(rol => {
        const n = catRolesNormalize(rol);
        n.empresa = n.idEmpresa && empIndex[n.idEmpresa] ? empIndex[n.idEmpresa].nombre : (n.idEmpresa ? 'Not Found' : 'Global');
        return n;
      });
      setRows(normalized);
    }
    catch (e) {
      console.error(e);
      triggerAlert('Error al cargar roles');
    }
    finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.nombre || '').toLowerCase().includes(q) ||
      (r.descripcion || '').toLowerCase().includes(q)
    );
  }, [rows, query]);

  // clamp page cuando cambie el filtro o los datos
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    // si ya está abierto en modo "new", actúa como toggle para cerrar
    if (modalOpen && modalMode === 'new') {
      setModalOpen(false);
      setSelectedId(null);
      setEditData(null);
      return;
    }

    // abrir limpio en modo "new"
    setSelectedId(null);
    setEditData({ idRol: 0, nombre: '', descripcion: '' });
    setModalMode('new');
    setModalOpen(true);
  };

  // GET by id (editar)
  const handleEdit = async (id) => {
    // Nada de llamadas a la API; ya lo tenemos en memoria
    const r = rows.find(x => x.idRol === id);
    if (!r) {
      triggerAlert('No se encontró el rol en memoria.');
      return;
    }
    const mapped = {
      idRol: r.idRol,
      nombre: r.nombre ?? '',
      descripcion: r.descripcion ?? '',
    };
    setSelectedId(id);
    setEditData(mapped);
    setModalMode('edit');
    setModalOpen(true);
  };


  // SUBMIT (new/edit)
  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);
      if (mode === 'new') {
        await axios.post(ENDPOINTS.roles, {
          nombreRol: payload.nombre,
          descripcion: payload.descripcion,
          esSistema: false,
          idEmpresa: getSessionEmpresaId()
        });
      }
      else {
        await axios.put(ENDPOINTS.roles, {
          idRol: selectedId,
          nombreRol: payload.nombre,
          descripcion: payload.descripcion,
          esSistema: false,
          idEmpresa: getSessionEmpresaId()
        });
      }
      setModalOpen(false);
      await loadData();
    }
    catch (e) {
      console.error(e);
      triggerAlert('Error al guardar el rol');
    }
    finally {
      setLoading(false);
    }
  };


  const askDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(`${ENDPOINTS.roles}/${deleteTarget.id}`);
      setDeleteOpen(false);
      await loadData();
    }
    catch (e) {
      console.error(e);
      triggerAlert('Error al eliminar el rol');
    }
    finally {
      setLoading(false);
    }
  };

  // ---------- Render ----------
  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const keysData = (() => {
    if (!rows || rows.length === 0) return [];

    let keys = getKeysData(rows[0], {
      exclude: ['activo', 'idEmpresa']
    });

    return keys;
  })([rows]);
  const formatedKeys = formatKeys(keysData);

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">Catálogo de Roles</h2>

          <input
            className="roles-search"
            placeholder="Buscar por nombre…"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />
          <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
        </div>

        {/* Slot del editor */}
        {modalOpen && (
          <div className="roles-editor-slot">
            <RoleModal
              open={modalOpen}
              mode={modalMode}
              data={editData}
              inline
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
            />
          </div>
        )}

        {/* Tabla */}
        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  {formatedKeys.map(key => (<th style={{ textAlign: 'center' }}>{key}</th>))}
                  <th style={{ textAlign: 'center' }}>Editar</th>
                  <th style={{ textAlign: 'center' }}>Borrar</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => (
                  <tr key={r.idRol}>
                    {keysData.map(key =>
                      (<td style={{ textAlign: 'center' }}>{normalizeValue(r[key])}</td>)
                    )}
                    <td style={{ textAlign: 'center' }}>
                      <button className="roles-btn roles-btn--edit" onClick={() => handleEdit(r.idRol)}>✎ Editar</button>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button className="roles-btn roles-btn--del" onClick={() => askDelete(r.idRol, r.nombre)}>🗑️ Borrar</button>
                    </td>
                  </tr>
                ))}
                {!loading && paged.length === 0 && (
                  <tr><td colSpan={5}>Sin resultados.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Paginación */}
        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0
              ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
              : <span>0 resultados</span>}
          </div>

          <div className="roles-pagination-right">
            <PageBtn
              label="‹"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            />
            {pageNumbers.map(n => (
              <PageBtn
                key={n}
                label={n}
                onClick={() => setPage(n)}
                active={n === page}
              />
            ))}
            <PageBtn
              label="›"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            />
          </div>
        </div>

        {loading && <div style={{ marginTop: '.8rem', color: '#0a74b9', fontWeight: 700 }}>Cargando…</div>}
      </div>

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />

      <AlertDialog
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </div>
  );
}